/* eslint-disable react/prop-types */
import { AlertCircle, RefreshCw, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * PendingPayLinkConflictDialog
 * Modal shown when the backend returns error_code === "pay_link_already_pending".
 * Offers the user the option to:
 * 1. Cancel previous pending link and immediately send a new one
 * 2. Only cancel the previous pending link
 * 3. Keep the existing link (Dismiss)
 */
export default function PendingPayLinkConflictDialog({
  isOpen,
  onClose,
  title = "Active Payment Link Already Exists",
  orderLabel = "",
  existingPayLink,
  targetChannel = "email",
  onCancelAndResend,
  onCancelOnly,
  isLoading = false,
}) {
  const channelDisplay = existingPayLink?.channel ? existingPayLink.channel.toUpperCase() : "EMAIL/SMS";
  const targetChannelDisplay = targetChannel ? targetChannel.toUpperCase() : "EMAIL";

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const d = new Date(dateString);
      return isNaN(d.getTime()) ? null : d.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[490px] bg-cblack4 border border-amber-500/40 text-white p-6 shadow-2xl rounded-xl animate-fadeIn">
        <DialogHeader className="flex flex-col justify-center items-center gap-3 text-center">
          {/* Warning Icon Badge */}
          <div className="relative flex justify-center items-center h-14 w-14 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 shadow-inner">
            <AlertCircle className="h-7 w-7" />
          </div>

          <DialogTitle className="text-white text-xl font-bold tracking-wide">
            {title}
          </DialogTitle>

          {orderLabel && (
            <p className="text-cyellow font-semibold text-sm -mt-1">{orderLabel}</p>
          )}

          <DialogDescription className="text-cwhite3 text-xs leading-relaxed max-w-[390px]">
            A pay-by-link payment request is already pending for this item. You must cancel the active link before a new one can be generated.
          </DialogDescription>
        </DialogHeader>

        {/* Existing Link Details */}
        <div className="my-3 space-y-3">
          <div className="bg-cblack3 p-3.5 rounded-xl border border-gray-700/80 space-y-2 text-xs">
            <div className="flex justify-between items-center pb-2 border-b border-gray-700/50">
              <span className="text-cwhite3">Existing Channel:</span>
              <span className="font-semibold text-white bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded uppercase">
                {channelDisplay}
              </span>
            </div>

            {existingPayLink?.provider_key && (
              <div className="flex justify-between items-center pb-2 border-b border-gray-700/50">
                <span className="text-cwhite3">Payment Provider:</span>
                <span className="font-semibold text-white capitalize">
                  {existingPayLink.provider_key}
                </span>
              </div>
            )}

            {existingPayLink?.created_at && (
              <div className="flex justify-between items-center pb-2 border-b border-gray-700/50">
                <span className="text-cwhite3">Sent At:</span>
                <span className="text-cwhite2 font-medium">
                  {formatDate(existingPayLink.created_at) || existingPayLink.created_at}
                </span>
              </div>
            )}

            {existingPayLink?.expires_at && (
              <div className="flex justify-between items-center">
                <span className="text-cwhite3">Expires At:</span>
                <span className="text-red-400 font-medium">
                  {formatDate(existingPayLink.expires_at) || existingPayLink.expires_at}
                </span>
              </div>
            )}
          </div>

          <div className="bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs p-3 rounded-lg flex items-start gap-2.5">
            <span className="text-base">💡</span>
            <span>
              Would you like to cancel the existing link and send a new link via <strong>{targetChannelDisplay}</strong>?
            </span>
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-col gap-2 mt-2">
          {/* Action 1: Cancel Old & Send New Link */}
          <Button
            type="button"
            isLoading={isLoading}
            disabled={isLoading}
            className="w-full bg-cyellow hover:bg-cyellow/90 text-black font-bold flex items-center justify-center gap-2 py-2.5"
            onClick={onCancelAndResend}
          >
            <RefreshCw className="h-4 w-4" />
            Cancel Old & Send New Link ({targetChannelDisplay})
          </Button>

          <div className="flex gap-2 w-full mt-1">
            {/* Action 2: Only Cancel Old Link */}
            {onCancelOnly && (
              <Button
                type="button"
                variant="outline"
                disabled={isLoading}
                className="flex-1 bg-cred/10 border-cred/40 text-cred hover:bg-cred/20 hover:text-red-300 text-xs font-semibold flex items-center justify-center gap-1.5"
                onClick={onCancelOnly}
              >
                <XCircle className="h-3.5 w-3.5" />
                Cancel Old Link Only
              </Button>
            )}

            {/* Action 3: Keep existing link / Close */}
            <Button
              type="button"
              variant="outline"
              className="flex-1 bg-transparent border-gray-700 text-cwhite2 hover:bg-cblack3 text-xs"
              onClick={onClose}
              disabled={isLoading}
            >
              Keep Existing Link
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
